<template>
  <div>
    <div class="back-row">
      <button class="btn-back" @click="$router.go(-1)">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>

    <div class="page-header">
      <div>
        <h5 class="page-title">{{ isEdit ? 'Edit Requisition' : 'New Purchase Requisition' }}</h5>
        <p class="page-sub">{{ isEdit ? 'Update the details below' : 'Fill in the requisition details and add line items' }}</p>
      </div>
      <div class="header-meta" v-if="isEdit && form.pr_number">
        <span class="mono">{{ form.pr_number }}</span>
        <span :class="['status-badge', `status-badge--${form.status}`]">{{ formatStatus(form.status) }}</span>
      </div>
    </div>

    <!-- Step indicator -->
    <div class="stepper-card">
      <div class="stepper">
        <div v-for="(step, i) in steps" :key="step.key" class="step-wrap">
          <div class="step">
            <div :class="['step-circle', stepClass(i)]">
              <font-awesome-icon v-if="i < currentStep" icon="check" />
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span :class="['step-label', currentStep === i ? 'step-label--active' : 'step-label--pending']">{{ step.label }}</span>
          </div>
          <div v-if="i < steps.length - 1" :class="['step-line', i < currentStep ? 'step-line--done' : 'step-line--pending']"></div>
        </div>
      </div>
      <div class="step-mobile-label">Step {{ currentStep + 1 }} of {{ steps.length }} &mdash; {{ steps[currentStep].label }}</div>
    </div>

    <!-- Step 0: General Info -->
    <div v-if="currentStep === 0" class="form-card">

      <!-- Procurement destination -->
      <h6 class="section-title">Procurement Destination</h6>
      <div class="dest-toggle">
        <button type="button"
          :class="['dest-btn', form.procurement_type === '' ? 'dest-btn--active' : '']"
          @click="setDestType('')">
          <font-awesome-icon icon="list" class="dest-icon" /> General
        </button>
        <button type="button"
          :class="['dest-btn', form.procurement_type === 'project' ? 'dest-btn--active' : '']"
          @click="setDestType('project')">
          <font-awesome-icon icon="folder-open" class="dest-icon" /> Project
        </button>
        <button type="button"
          :class="['dest-btn', form.procurement_type === 'department' ? 'dest-btn--active' : '']"
          @click="setDestType('department')">
          <font-awesome-icon icon="building" class="dest-icon" /> Department
        </button>
      </div>

      <div v-if="form.procurement_type === 'project'" class="dest-field">
        <label class="field-label">Project <span class="req">*</span></label>
        <select v-model="form.project_id" class="field-input field-select" :class="{ 'field-input--error': errors.project_id }">
          <option value="">— Select project —</option>
          <option v-for="p in projects" :key="p.id" :value="p.id">
            {{ p.project_code }} — {{ p.project_name }}
          </option>
        </select>
        <span class="field-error" v-if="errors.project_id">{{ errors.project_id }}</span>
      </div>

      <div v-if="form.procurement_type === 'department'" class="dest-field">
        <label class="field-label">Department <span class="req">*</span></label>
        <select v-model="form.department_id" class="field-input field-select" :class="{ 'field-input--error': errors.department_id }">
          <option value="">— Select department —</option>
          <option :value="d.id" v-for="d in departments" :key="d.id">{{ d.name }}</option>
        </select>
        <span class="field-error" v-if="errors.department_id">{{ errors.department_id }}</span>
      </div>

      <div class="section-divider"></div>

      <h6 class="section-title">Requisition Details</h6>
      <div class="type-row">
        <label class="field-label">Item Type</label>
        <div class="type-toggle">
          <button type="button" :class="['type-btn', form.item_type === 'goods' ? 'type-btn--active' : '']" @click="setItemType('goods')">
            <font-awesome-icon icon="box" class="me-1" /> Goods
          </button>
          <button type="button" :class="['type-btn', form.item_type === 'service' ? 'type-btn--active' : '']" @click="setItemType('service')">
            <font-awesome-icon icon="tools" class="me-1" /> Service
          </button>
          <button type="button" :class="['type-btn', form.item_type === 'expense' ? 'type-btn--active' : '']" @click="setItemType('expense')">
            <font-awesome-icon icon="receipt" class="me-1" /> Expense
          </button>
        </div>
      </div>
      <div class="fields-grid">
        <div class="field-group field-full">
          <label class="field-label">Title / Summary <span class="req">*</span></label>
          <input v-model="form.title" class="field-input" :class="{ 'field-input--error': errors.title }" placeholder="What are you requesting?" />
          <span class="field-error" v-if="errors.title">{{ errors.title }}</span>
        </div>
        <div class="field-group">
          <label class="field-label">Category</label>
          <select v-model="form.category_id" class="field-input field-select">
            <option value="">Select category</option>
            <option :value="c.id" v-for="c in categories" :key="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="field-group">
          <label class="field-label">Location <span class="req">*</span></label>
          <select v-model="form.location_id" class="field-input field-select" :class="{ 'field-input--error': errors.location_id }">
            <option value="">Select location</option>
            <option :value="l.id" v-for="l in locations" :key="l.id">{{ l.store_name }}</option>
          </select>
          <span class="field-error" v-if="errors.location_id">{{ errors.location_id }}</span>
        </div>
        <div class="field-group">
          <label class="field-label">Urgency</label>
          <select v-model="form.urgency" class="field-input field-select">
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
            <option value="emergency">Emergency</option>
          </select>
        </div>
        <div class="field-group">
          <label class="field-label">Need By Date</label>
          <input v-model="form.need_by_date" type="date" class="field-input" :min="today" />
        </div>
        <div class="field-group">
          <label class="field-label">Currency</label>
          <select v-model="form.currency" class="field-input field-select">
            <option value="">Select currency</option>
            <option v-for="c in currencies" :key="c.id" :value="c.code || c.name">{{ c.name }}</option>
          </select>
        </div>
        <div class="field-group">
          <label class="field-label">Cost Code <span class="optional">optional</span></label>
          <input v-model="form.cost_code" class="field-input" placeholder="e.g. CAPEX-2026-001" />
        </div>
        <div class="field-group field-full">
          <label class="field-label">Description <span class="optional">optional</span></label>
          <textarea v-model="form.description" class="field-input" rows="3" placeholder="Additional context or requirements..."></textarea>
        </div>
        <div class="field-group field-full">
          <label class="field-label">Notes <span class="optional">optional</span></label>
          <textarea v-model="form.notes" class="field-input" rows="2" placeholder="Internal notes..."></textarea>
        </div>
      </div>
    </div>

    <!-- Step 1: Line Items -->
    <div v-if="currentStep === 1" class="form-card">
      <div class="section-head">
        <h6 class="section-title">Line Items</h6>
        <button class="btn-outline btn-sm" @click="addLine">
          <font-awesome-icon icon="plus" class="me-1" /> Add Line
        </button>
      </div>

      <div v-if="!form.lines.length" class="empty-lines">
        <p>No line items yet. Click "Add Line" to start.</p>
      </div>

      <div v-for="(line, i) in form.lines" :key="i" class="line-card">
        <div class="line-header">
          <span class="line-num">Line {{ i + 1 }}</span>
          <button class="btn-remove-line" @click="removeLine(i)" title="Remove"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="fields-grid">
          <div class="field-group field-full" v-if="isGoods && products.length">
            <label class="field-label">Product <span class="optional">optional — auto-fills fields below</span></label>
            <Multiselect
              v-model="line.procurement_product_id"
              label="name"
              :options="productOptions"
              :searchable="true"
              :close-on-select="true"
              :create-option="false"
              :hideSelected="false"
              placeholder="Search product..."
              class="pr-product-multiselect"
              @change="val => applyProduct(i, val)"
            />
          </div>
          <div class="field-group field-full">
            <label class="field-label">{{ isGoods ? 'Description' : 'Item / Service Name' }} <span class="req">*</span></label>
            <input v-model="line.description" class="field-input" :class="{ 'field-input--error': lineErrors[i]?.description }" :placeholder="isGoods ? 'Item description / specification' : 'Enter service or expense name'" />
            <span class="field-error" v-if="lineErrors[i]?.description">{{ lineErrors[i].description }}</span>
          </div>
          <div class="field-group">
            <label class="field-label">Category</label>
            <select v-model="line.category_id" class="field-input field-select">
              <option value="">Select</option>
              <option :value="c.id" v-for="c in categories" :key="c.id">{{ c.name }}</option>
            </select>
          </div>
          <div class="field-group">
            <label class="field-label">Quantity <span class="req">*</span></label>
            <input v-model.number="line.quantity" type="number" min="0.0001" step="any" class="field-input" :class="{ 'field-input--error': lineErrors[i]?.quantity }" @input="recalcLine(i)" />
            <span class="field-error" v-if="lineErrors[i]?.quantity">{{ lineErrors[i].quantity }}</span>
          </div>
          <div class="field-group" v-if="isGoods">
            <label class="field-label">UOM</label>
            <input v-model="line.uom" class="field-input" placeholder="e.g. pcs, kg, m" list="uom-list" />
            <datalist id="uom-list">
              <option v-for="u in uoms" :key="u.id" :value="u.name">{{ u.name }}</option>
            </datalist>
          </div>
          <div class="field-group">
            <label class="field-label">Est. Unit Cost</label>
            <input v-model.number="line.estimated_unit_cost" type="number" min="0" step="any" class="field-input" @input="recalcLine(i)" />
          </div>
          <div class="field-group">
            <label class="field-label">Est. Total</label>
            <input :value="formatLineTotal(line)" class="field-input" readonly style="background:#f8fafc;color:#7a90b0;" />
          </div>
          <div class="field-group field-full">
            <label class="field-label">Specification <span class="optional">optional</span></label>
            <input v-model="line.specification" class="field-input" placeholder="Technical spec, model, standard..." />
          </div>
        </div>
      </div>

      <!-- Grand total -->
      <div class="total-row" v-if="form.lines.length">
        <span class="total-label">Estimated Total</span>
        <span class="total-value">{{ formatCurrency(grandTotal, form.currency) }}</span>
      </div>
    </div>

    <!-- Navigation -->
    <div class="form-nav">
      <button class="btn-outline" :disabled="saving" @click="prevStep" v-if="currentStep > 0">
        <font-awesome-icon icon="arrow-left" class="me-1" /> Back
      </button>
      <button class="btn-outline" :disabled="saving" @click="$router.go(-1)" v-else>Cancel</button>

      <div class="nav-right">
        <button class="btn-outline" :disabled="saving" @click="saveDraft" v-if="isEdit && form.status === 'draft'">
          <span v-if="saving === 'draft'" class="spinner-sm"></span>
          {{ saving === 'draft' ? 'Saving...' : 'Save Draft' }}
        </button>

        <button v-if="currentStep < steps.length - 1" class="btn-primary" @click="nextStep">
          Next <font-awesome-icon icon="arrow-right" class="ms-1" />
        </button>

        <button v-else class="btn-primary" :disabled="!!saving" @click="savePR">
          <span v-if="saving === 'save'" class="spinner-sm"></span>
          {{ saving === 'save'
              ? 'Submitting...'
              : (isEdit && !['draft','returned'].includes(form.status) ? 'Update Requisition' : (isEdit ? 'Update & Submit' : 'Create & Submit')) }}
        </button>

        <button v-if="false" :disabled="!!saving" @click="saveAndSubmit">
          <span v-if="saving === 'submit'" class="spinner-sm"></span>
          {{ saving === 'submit' ? 'Submitting...' : 'Save & Submit' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

export default {
  name: 'PrForm',
  components: { Multiselect },
  setup() { return { toast: useToast() }; },

  data() {
    return {
      isEdit: !!this.$route.params.id,
      currentStep: 0,
      steps: [
        { key: 'general',  label: 'General Info' },
        { key: 'lines',    label: 'Line Items' },
      ],
      form: {
        pr_number: '', status: 'draft',
        title: '', category_id: '', description: '', notes: '',
        location_id: '', department_id: '',
        procurement_type: '', project_id: '',
        project: '', cost_code: '', need_by_date: '',
        urgency: 'normal', currency: '', item_type: 'goods',
        lines: [],
      },
      errors: {}, lineErrors: [],
      categories: [], currencies: [], uoms: [], products: [],
      locations: [], departments: [], projects: [],
      saving: false,
      today: new Date().toISOString().split('T')[0],
    };
  },

  watch: {
    'form.project_id'(id) {
      if (!id) return;
      const proj = this.projects.find(p => p.id === id);
      if (proj?.location_id) {
        this.form.location_id = proj.location_id;
      }
    },
    'form.item_type'(newType) {
      if (newType !== 'goods') {
        this.form.lines.forEach(l => { l.procurement_product_id = ''; });
      }
    },
  },

  computed: {
    grandTotal() {
      return this.form.lines.reduce((sum, l) => sum + (Number(l.estimated_unit_cost || 0) * Number(l.quantity || 0)), 0);
    },
    productOptions() {
      return this.products.map(p => ({ value: p.id, name: p.product_name, uom: p.unit_of_measurement }));
    },
    isGoods() { return this.form.item_type === 'goods'; },
  },

  mounted() {
    this.fetchLookups();
    if (this.isEdit) {
      this.fetchPR();
    } else {
      // Pre-fill from project-detail "New PR for this Project" navigation
      const q = this.$route.query;
      if (q.project_id) {
        this.form.procurement_type = 'project';
        this.form.project_id       = q.project_id;
      }
    }
  },

  methods: {
    async fetchLookups() {
      const [cats, curr, uoms, products, locs, depts, projs] = await Promise.allSettled([
        axios.get('/procurement/categories'),
        axios.get('/settings/currency'),
        axios.get('/settings/unit-of-measurement'),
        axios.get('/inventory/product/active/all'),
        axios.get('/admin/store/all'),
        axios.get('/payroll/department/active'),
        axios.get('/procurement/projects/dropdown'),
      ]);
      if (cats.status === 'fulfilled')      this.categories          = cats.value.data.data ?? [];
      if (curr.status === 'fulfilled')      this.currencies          = curr.value.data?.data?.data ?? curr.value.data?.data ?? [];
      if (uoms.status === 'fulfilled')      this.uoms                = uoms.value.data?.data ?? [];
      if (products.status === 'fulfilled')  this.products            = products.value.data?.data ?? [];
      if (locs.status === 'fulfilled')      this.locations           = locs.value.data?.data ?? [];
      if (depts.status === 'fulfilled')     this.departments         = depts.value.data?.data ?? [];
      if (projs.status === 'fulfilled')     this.projects            = projs.value.data?.data ?? [];
    },

    async fetchPR() {
      try {
        const { data } = await axios.get(`/procurement/purchase-requisitions/${this.$route.params.id}`);
        const pr = data.data;
        this.form = {
          pr_number:        pr.pr_number,
          status:           pr.status,
          title:            pr.title,
          category_id:      pr.category_id   ?? '',
          location_id:      pr.location_id   ?? '',
          department_id:    pr.department_id  ?? '',
          procurement_type: pr.procurement_type ?? '',
          project_id:       pr.project_id     ?? '',
          description:      pr.description   ?? '',
          notes:            pr.notes         ?? '',
          project:          pr.project       ?? '',
          cost_code:        pr.cost_code     ?? '',
          need_by_date:     pr.need_by_date  ?? '',
          urgency:          pr.urgency,
          currency:         pr.currency      ?? '',
          item_type:        pr.item_type     ?? 'goods',
          lines: (pr.lines ?? []).map(l => ({
            procurement_product_id: l.procurement_product_id ?? '',
            category_id:            l.category_id ?? '',
            description:            l.description,
            specification:          l.specification ?? '',
            quantity:               l.quantity,
            uom:                    l.uom ?? '',
            estimated_unit_cost:    l.estimated_unit_cost,
          })),
        };
      } catch { this.toast.error('Failed to load requisition'); this.$router.go(-1); }
    },

    setDestType(type) {
      this.form.procurement_type = type;
      if (type !== 'project')    this.form.project_id    = '';
      if (type !== 'department') this.form.department_id = '';
      delete this.errors.project_id;
      delete this.errors.department_id;
    },

    setItemType(type) {
      this.form.item_type = type;
    },

    addLine() {
      this.form.lines.push({ procurement_product_id: '', category_id: '', description: '', specification: '', quantity: 1, uom: '', estimated_unit_cost: 0 });
    },

    applyProduct(lineIndex, val) {
      const productId = val ?? this.form.lines[lineIndex].procurement_product_id;
      if (!productId) return;
      const p = this.productOptions.find(x => x.value === productId);
      if (!p) return;
      const line = this.form.lines[lineIndex];
      line.description = p.name;
      line.uom         = p.uom || line.uom || '';
    },

    removeLine(i) { this.form.lines.splice(i, 1); this.lineErrors.splice(i, 1); },

    recalcLine() { /* computed on render */ },

    formatLineTotal(line) {
      const t = Number(line.quantity || 0) * Number(line.estimated_unit_cost || 0);
      return this.formatCurrency(t, this.form.currency);
    },

    formatCurrency(amount, currency) {
      const sym = { NGN:'₦', GBP:'£', USD:'$', EUR:'€' }[currency] ?? (currency ?? '');
      return sym + Number(amount || 0).toLocaleString('en', { minimumFractionDigits:2, maximumFractionDigits:2 });
    },

    stepClass(i) {
      if (i < this.currentStep) return 'step-circle--done';
      if (i === this.currentStep) return 'step-circle--active';
      return 'step-circle--pending';
    },

    validateStep0() {
      this.errors = {};
      if (!this.form.title.trim())    this.errors.title       = 'Title is required';
      if (!this.form.location_id)     this.errors.location_id = 'Location is required';
      if (this.form.procurement_type === 'project'    && !this.form.project_id)    this.errors.project_id    = 'Project is required';
      if (this.form.procurement_type === 'department' && !this.form.department_id) this.errors.department_id = 'Department is required';
      return !Object.keys(this.errors).length;
    },

    validateStep1() {
      this.lineErrors = this.form.lines.map(l => {
        const e = {};
        if (!l.description.trim()) e.description = 'Description is required';
        if (!l.quantity || l.quantity <= 0) e.quantity = 'Quantity must be greater than 0';
        return e;
      });
      if (!this.form.lines.length) { this.toast.error('Add at least one line item'); return false; }
      return this.lineErrors.every(e => !Object.keys(e).length);
    },

    nextStep() {
      if (this.currentStep === 0 && !this.validateStep0()) return;
      this.currentStep++;
    },

    prevStep() { this.currentStep--; },

    async saveDraft() {
      this.saving = 'draft';
      try {
        await axios.patch(`/procurement/purchase-requisitions/${this.$route.params.id}/draft`, this.buildPayload());
        this.toast.success('Draft saved');
      } catch (e) { this.toast.error(e.response?.data?.message || 'Failed to save draft'); }
      finally { this.saving = false; }
    },

    async savePR() {
      if (!this.validateStep0() | !this.validateStep1()) { this.currentStep = this.errors.title ? 0 : 1; return; }
      this.saving = 'save';
      try {
        if (this.isEdit) {
          const id = this.$route.params.id;
          await axios.put(`/procurement/purchase-requisitions/${id}`, this.buildPayload());
          if (['draft', 'returned'].includes(this.form.status)) {
            await axios.post(`/procurement/purchase-requisitions/${id}/submit`);
            this.toast.success('Requisition updated and submitted for approval');
          } else {
            this.toast.success('Requisition updated');
          }
          this.$router.replace(`/procurement/purchase-requisitions/${id}`);
        } else {
          const { data } = await axios.post('/procurement/purchase-requisitions', this.buildPayload());
          await axios.post(`/procurement/purchase-requisitions/${data.data.id}/submit`);
          this.toast.success('Requisition created and submitted for approval');
          this.$router.replace(`/procurement/purchase-requisitions/${data.data.id}`);
        }
      } catch (e) { this.toast.error(e.response?.data?.message || 'Failed to save'); }
      finally { this.saving = false; }
    },

    async saveAndSubmit() {
      if (!this.validateStep0() || !this.validateStep1()) { this.currentStep = this.errors.title ? 0 : 1; return; }
      this.saving = 'submit';
      try {
        const { data } = await axios.post('/procurement/purchase-requisitions', this.buildPayload());
        await axios.post(`/procurement/purchase-requisitions/${data.data.id}/submit`);
        this.toast.success('Requisition created and submitted for approval');
        this.$router.replace(`/procurement/purchase-requisitions/${data.data.id}`);
      } catch (e) { this.toast.error(e.response?.data?.message || 'Failed'); }
      finally { this.saving = false; }
    },

    buildPayload() {
      const type = this.form.procurement_type || null;
      return {
        title:            this.form.title,
        category_id:      this.form.category_id   || null,
        location_id:      this.form.location_id   || null,
        procurement_type: type,
        project_id:       type === 'project'    ? (this.form.project_id    || null) : null,
        department_id:    type === 'department' ? (this.form.department_id || null) : null,
        description:      this.form.description || null,
        notes:            this.form.notes       || null,
        project:          this.form.project     || null,
        cost_code:        this.form.cost_code   || null,
        need_by_date:     this.form.need_by_date || null,
        urgency:          this.form.urgency,
        currency:         this.form.currency    || null,
        item_type:        this.form.item_type   || 'goods',
        lines:            this.form.lines,
      };
    },

    formatStatus(s) {
      const map = { draft:'Draft', submitted:'Submitted', under_review:'Under Review', returned:'Returned',
        approved:'Approved', sourcing:'Sourcing', partially_sourced:'Partially Sourced',
        fully_sourced:'Fully Sourced', closed:'Closed', cancelled:'Cancelled' };
      return map[s] ?? s;
    },
  },
};
</script>

<style scoped>
.back-row    { margin-bottom:14px; }
.btn-back    { background:transparent; border:none; color:#7a90b0; font-size:13px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:7px; padding:0; }
.btn-back:hover { color:#004AAD; }

.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.header-meta  { display:flex; align-items:center; gap:10px; }
.mono         { font-family:monospace; font-size:13px; color:#004AAD; font-weight:700; }
.me-1         { margin-right:4px; }
.ms-1         { margin-left:4px; }

/* Stepper */
.stepper-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:20px 24px; margin-bottom:20px; }
.stepper       { display:flex; align-items:center; justify-content:center; }
.step-wrap     { display:flex; align-items:center; }
.step          { display:flex; flex-direction:column; align-items:center; gap:8px; }
.step-circle   { width:40px; height:40px; border-radius:50%; border:2px solid; display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:700; flex-shrink:0; transition:all .2s; }
.step-circle--done    { background:#004AAD; border-color:#004AAD; color:#fff; }
.step-circle--active  { background:#fff; border-color:#004AAD; color:#004AAD; }
.step-circle--pending { background:#fff; border-color:#dde5f0; color:#94a3b8; }
.step-label           { font-size:12px; font-weight:600; white-space:nowrap; }
.step-label--active   { color:#1a2b45; }
.step-label--pending  { color:#94a3b8; }
.step-line            { width:60px; height:2px; margin:0 8px; margin-bottom:22px; flex-shrink:0; }
.step-line--done      { background:#004AAD; }
.step-line--pending   { background:#e4eaf2; }
.step-mobile-label    { display:none; text-align:center; margin-top:12px; font-size:13px; font-weight:600; color:#1a2b45; }

/* Form card */
.form-card     { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:28px 32px; margin-bottom:16px; }
.section-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 20px; }
.section-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }

/* Procurement destination toggle */
.dest-toggle   { display:flex; gap:10px; margin-bottom:16px; flex-wrap:wrap; }
.dest-btn      { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:1.5px solid #dde5f0; border-radius:9px; background:#fff; font-size:13px; font-weight:600; color:#5a6a85; cursor:pointer; transition:all .15s; }
.dest-btn:hover { background:#f4f7fb; border-color:#c5d6f5; color:#1a2b45; }
.dest-btn--active { background:#004AAD; border-color:#004AAD; color:#fff; }
.dest-icon     { font-size:12px; }
.dest-field    { margin-bottom:20px; display:flex; flex-direction:column; gap:5px; max-width:480px; }
.section-divider { border:none; border-top:1px solid #e4eaf2; margin:20px 0; }

/* Item type toggle */
.type-row    { display:flex; align-items:center; gap:14px; margin-bottom:18px; flex-wrap:wrap; }
.type-toggle { display:flex; gap:8px; flex-wrap:wrap; }
.type-btn    { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border:1.5px solid #dde5f0; border-radius:9px; background:#fff; font-size:13px; font-weight:600; color:#5a6a85; cursor:pointer; transition:all .15s; }
.type-btn:hover { background:#f4f7fb; border-color:#c5d6f5; color:#1a2b45; }
.type-btn--active { background:#004AAD; border-color:#004AAD; color:#fff; }

/* Fields */
.fields-grid   { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.field-group   { display:flex; flex-direction:column; gap:5px; }
.field-full    { grid-column:1/-1; }
.field-label   { font-size:13px; font-weight:600; color:#1a2b45; }
.req           { color:#e03535; }
.optional      { font-size:11px; color:#aab8cc; font-weight:400; margin-left:4px; }
.field-input   { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; font-family:inherit; box-sizing:border-box; transition:border-color .15s; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-input--error { border-color:#e03535; }
.field-select  { appearance:auto; cursor:pointer; }
.field-error   { font-size:11.5px; color:#e03535; }

/* Lines */
.empty-lines   { text-align:center; padding:32px; color:#aab8cc; font-size:13px; border:1.5px dashed #dde5f0; border-radius:10px; }
.line-card     { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:18px; margin-bottom:14px; }
.line-header   { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.line-num      { font-size:12px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.05em; }
.btn-remove-line { background:transparent; border:none; color:#dc2626; cursor:pointer; font-size:14px; padding:4px; }
.btn-remove-line:hover { color:#b91c1c; }

.total-row  { display:flex; align-items:center; justify-content:flex-end; gap:16px; padding:16px 0 4px; border-top:1px solid #e4eaf2; margin-top:8px; }
.total-label { font-size:13px; font-weight:700; color:#7a90b0; text-transform:uppercase; letter-spacing:.04em; }
.total-value { font-size:20px; font-weight:800; color:#1a2b45; }

/* Nav */
.form-nav   { display:flex; align-items:center; justify-content:space-between; padding-top:4px; gap:12px; }
.nav-right  { display:flex; gap:10px; flex-wrap:wrap; }

.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13.5px; font-weight:600; padding:11px 24px; border-radius:9px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }

.btn-submit { display:inline-flex; align-items:center; gap:6px; background:#16a34a; color:#fff; font-size:13.5px; font-weight:600; padding:11px 24px; border-radius:9px; border:none; cursor:pointer; }
.btn-submit:hover:not(:disabled) { background:#15803d; }
.btn-submit:disabled { opacity:.5; cursor:not-allowed; }

.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13.5px; font-weight:600; padding:11px 20px; border-radius:9px; cursor:pointer; }
.btn-outline:hover:not(:disabled) { background:#f4f7fb; }
.btn-outline:disabled { opacity:.45; cursor:not-allowed; }
.btn-sm     { padding:7px 14px; font-size:12.5px; }

.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }

.status-badge { display:inline-block; font-size:11.5px; font-weight:600; border-radius:20px; padding:3px 10px; }
.status-badge--draft     { background:#f1f5f9; color:#64748b; }
.status-badge--returned  { background:#fef9c3; color:#854d0e; }

@media(max-width:600px) {
  .form-card    { padding:18px 16px; }
  .fields-grid  { grid-template-columns:1fr; }
  .field-full   { grid-column:1; }
  .step-label   { display:none; }
  .step-circle  { width:32px; height:32px; font-size:12px; }
  .step-line    { width:20px; }
  .step-mobile-label { display:block; }
  .form-nav     { flex-direction:column-reverse; }
  .nav-right    { width:100%; }
  .btn-primary, .btn-outline, .btn-submit { width:100%; justify-content:center; }
}
.pr-product-multiselect { --ms-font-size:13.5px; --ms-line-height:1.4; --ms-min-height:38px; --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-ring-color:rgba(0,74,173,.08); --ms-dropdown-max-height:220px; }
</style>
